import {
    BookingFlowLayout,
    BookingFlowPageHeading,
    BookingFlowPrimaryButton,
    BookingFlowSelectableCard,
    bookingFlowSelectableCardStyles,
} from "components/booking-flow";
import useAnalytics from "hooks/analytics";
import useLanguage from "hooks/language";
import { FollowUpType } from "models/appointment";
import React, { useState } from "react";
import { trackBookingFlowConsultationSelected } from "utils/analytics/booking-flow-tracking";
import ServiceSelectV2 from "../home/screening/public-upfront-payments/service-select/service-select-v2";
import styles from "./consultation-selection-v2.module.css";
import { LabType } from "./ramqPopover";

type ConsultationSelectionV2Props = {
    labType: LabType;
    onSubmit: (followUpType: FollowUpType) => void;
};

export default function ConsultationSelectionV2({
    labType,
    onSubmit,
}: ConsultationSelectionV2Props): React.ReactElement {
    const [analytics] = useAnalytics();
    const { translations } = useLanguage();
    const tConsultation = translations.bookingFlow.consultation;
    const isPrivateLab = labType === "private";

    const [selectedFollowUp, setSelectedFollowUp] = useState<
        FollowUpType | undefined
    >(isPrivateLab ? FollowUpType.Express : undefined);

    const canContinue =
        selectedFollowUp === FollowUpType.Regular ||
        selectedFollowUp === FollowUpType.Express;

    const handleContinue = () => {
        if (!canContinue || !selectedFollowUp) {
            return;
        }
        trackBookingFlowConsultationSelected(analytics, selectedFollowUp);
        onSubmit(selectedFollowUp);
    };

    const feeSummaryText =
        selectedFollowUp === FollowUpType.Express
            ? tConsultation.feeSummaryExpress
            : selectedFollowUp === FollowUpType.Regular
            ? tConsultation.feeSummaryRegular
            : null;

    return (
        <BookingFlowLayout currentStep="consultation">
            <BookingFlowPageHeading
                title={
                    isPrivateLab
                        ? tConsultation.explainsResultsTitle
                        : tConsultation.receiveResultsTitle
                }
                subtitle={
                    isPrivateLab
                        ? tConsultation.explainsResultsSubtitle
                        : tConsultation.receiveResultsSubtitle
                }
                responsive
            />

            {isPrivateLab ? (
                <BookingFlowSelectableCard
                    checked={selectedFollowUp === FollowUpType.Express}
                    variant="dark"
                    onSelect={() => setSelectedFollowUp(FollowUpType.Express)}
                    headline={tConsultation.expressOnlineTitle}
                >
                    <div
                        style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 10,
                            flexWrap: "wrap",
                        }}
                    >
                        <span
                            className={`${bookingFlowSelectableCardStyles.priceChip} ${bookingFlowSelectableCardStyles.priceChipDark}`}
                        >
                            {tConsultation.expressResultsChip}
                        </span>
                    </div>
                    <p
                        className={
                            bookingFlowSelectableCardStyles.bodyTextLeadDark
                        }
                    >
                        {tConsultation.expressLead}
                    </p>
                    <p
                        className={`${bookingFlowSelectableCardStyles.bodyTextDark} ${bookingFlowSelectableCardStyles.bodyTextDarkTightTop}`}
                        dangerouslySetInnerHTML={{
                            __html: tConsultation.expressBody,
                        }}
                    />
                </BookingFlowSelectableCard>
            ) : (
                <ServiceSelectV2
                    selectedFollowUp={selectedFollowUp}
                    onSelectFollowUp={setSelectedFollowUp}
                />
            )}

            {!isPrivateLab && feeSummaryText ? (
                <div className={styles.feeSummary}>{feeSummaryText}</div>
            ) : null}

            <div className={styles.actions}>
                <BookingFlowPrimaryButton
                    disabled={!canContinue}
                    onClick={handleContinue}
                >
                    {tConsultation.continueToClinic}
                </BookingFlowPrimaryButton>
            </div>
        </BookingFlowLayout>
    );
}
